import { DeviceRegistryListener, MindSDK } from 'mind-sdk-web';
import { Camera as MindCamera } from 'mind-sdk-web/lib/Camera';
import { DeviceRegistry as MindDeviceRegistry } from 'mind-sdk-web/lib/DeviceRegistry';
import { Microphone as MindMicrophone } from 'mind-sdk-web/lib/Microphone';
import { Camera, Microphone, Screen, Speaker } from '../../../types';

export type MediaDevice = Microphone | Camera | Speaker;
export type MindMediaDevice = MindMicrophone | MindCamera;
export enum MediaDeviceKind {
  Microphone = 'audioinput',
  Camera = 'videoinput',
  Speaker = 'audiooutput',
}

export interface ExtendedDeviceRegistryListener extends DeviceRegistryListener {
  onDevicePermissionChanged(deviceKind: MediaDeviceKind, isDenied: boolean): void;
}

export class DeviceRegistry implements ExtendedDeviceRegistryListener {
  private mindDeviceRegistry: MindDeviceRegistry;
  private listener: ExtendedDeviceRegistryListener | null = null;

  private mediaDevices: MediaDeviceInfo[] = [];
  private microphones: Microphone[] = [];
  private cameras: Camera[] = [];
  private speakers: Speaker[] = [];

  constructor() {
    this.mindDeviceRegistry = MindSDK.getDeviceRegistry();
    this.mindDeviceRegistry.setListener(this);
    this.setupPermissionListeners();
  }

  private setupPermissionListeners(): void {
    // Listen for microphone permission changes
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((permissionStatus) => {
        permissionStatus.onchange = () => {
          this.onDevicePermissionChanged(MediaDeviceKind.Microphone, permissionStatus.state === 'denied');
        };
      })
      .catch(() => console.warn('Permissions API not supported'));

    // Listen for camera permission changes
    navigator.permissions
      .query({ name: 'camera' as PermissionName })
      .then((permissionStatus) => {
        permissionStatus.onchange = () => {
          this.onDevicePermissionChanged(MediaDeviceKind.Camera, permissionStatus.state === 'denied');
        };
      })
      .catch(() => console.warn('Permissions API not supported'));
  }

  onDeviceRegistryChanged(): void {
    this.update().catch((error) => {
      console.error('Error updating device registry:', error);
    });
  }

  onDevicePermissionChanged(deviceKind: MediaDeviceKind, isDenied: boolean): void {
    if (this.listener != null) {
      this.listener.onDevicePermissionChanged(deviceKind, isDenied);
    }
  }

  async update(): Promise<void> {
    this.mediaDevices = await navigator.mediaDevices.enumerateDevices();

    this.updateMicrophones();
    this.updateCameras();
    this.updateSpeakers(); // TODO check this twice! Is it possible to loose notification if speakers changed but Mind's model not changed?

    if (this.listener != null) {
      this.listener.onDeviceRegistryChanged(this);
    }
  }

  private updateMicrophones(): void {
    this.updateDevices({
      currentDevices: this.microphones,
      mindDevices: this.filterMediaDevices(this.mindDeviceRegistry.getMicrophones()),
      createDevice: (device) =>
        new Microphone({
          deviceRegistry: this,
          mindMicrophone: device,
          decoratedLabel: this.decorateDeviceLabel(device.getLabel()),
          description: this.getDefaultDeviceDescription(MediaDeviceKind.Microphone, device.getId()),
        }),
    });
  }

  private updateCameras(): void {
    this.updateDevices({
      currentDevices: this.cameras,
      mindDevices: this.filterMediaDevices(this.mindDeviceRegistry.getCameras()),
      createDevice: (device) =>
        new Camera({
          deviceRegistry: this,
          mindCamera: device,
          decoratedLabel: this.decorateDeviceLabel(device.getLabel()),
          description: this.getDefaultDeviceDescription(MediaDeviceKind.Camera, device.getId()),
        }),
    });
  }

  private updateDevices<T extends { getId(): string }>({
    currentDevices,
    mindDevices,
    createDevice,
  }: {
    currentDevices: { getId(): string }[];
    mindDevices: T[];
    createDevice: (device: T) => Microphone | Camera;
  }): void {
    // Remove unavailable devices
    for (let i = currentDevices.length - 1; i >= 0; i--) {
      const current = currentDevices[i];
      if (!mindDevices.find((d) => d.getId() === current.getId())) {
        currentDevices.splice(i, 1);
      }
    }

    // Add new devices
    mindDevices.forEach((device) => {
      const exists = currentDevices.some((d) => d.getId() === device.getId());
      if (!exists) {
        currentDevices.push(createDevice(device));
      }
    });
  }

  private updateSpeakers(): void {
    // Remove speakers that are no longer available
    for (let i = this.speakers.length - 1; i >= 0; i--) {
      const speaker = this.speakers[i];
      const mediaDevice = this.mediaDevices.find(
        (device) => device.kind === 'audiooutput' && device.deviceId === speaker.getId()
      );
      if (!mediaDevice) {
        this.speakers.splice(i, 1);
      }
    }

    // Check if default speaker exists, add it if not
    const defaultSpeakerExists = this.speakers.some((speaker) => speaker.getId() === 'default');
    if (!defaultSpeakerExists) {
      this.speakers.push(
        new Speaker({
          deviceRegistry: this,
          id: 'default',
          label: 'Default Speaker',
        })
      );
    }

    // Add new speakers
    this.mediaDevices.forEach((device) => {
      if (device.kind === MediaDeviceKind.Speaker && device.label !== '' && device.label !== 'default') {
        const speakerExists = this.speakers.some((speaker) => speaker.getId() === device.deviceId);
        if (!speakerExists) {
          this.speakers.push(
            new Speaker({
              deviceRegistry: this,
              id: device.deviceId,
              label: device.label,
            })
          );
        }
      }
    });
  }

  getMicrophones(): Microphone[] {
    return this.microphones;
  }

  getCameras(): Camera[] {
    return this.cameras;
  }

  getSpeakers(): Speaker[] {
    return this.filterMediaDevices(this.speakers);
  }

  getScreen(): Screen | null {
    const mindScreen = this.mindDeviceRegistry.getScreen();
    return mindScreen ? new Screen({ mindScreen: mindScreen }) : null;
  }

  getDevicesInfo(): MediaDeviceInfo[] {
    return this.mediaDevices;
  }

  setListener(listener: ExtendedDeviceRegistryListener | null): void {
    this.listener = listener;
  }

  isDefaultDevice(deviceId: string): boolean {
    const device = this.mediaDevices.find((device) => device.deviceId === deviceId);
    if (!device) {
      return false;
    }

    const groupDevices = this.mediaDevices.filter(
      (device) => device.kind === device.kind && device.groupId === device.groupId
    );
    return groupDevices.some((navDevice) => navDevice.deviceId === 'default');
  }

  private filterMediaDevices<T extends MindMediaDevice | Speaker>(devices: T[]): T[] {
    const unnecessaryIds = ['communications'];

    // 1. Filter out devices with invalid deviceId
    let filteredDevices = devices.filter((device) => !!device.getId());

    // 2. Filter out devices that have IDs in the unnecessary list
    filteredDevices = filteredDevices.filter((device) => !unnecessaryIds.includes(device.getId()));

    // 3. Filter out virtual devices
    filteredDevices = filteredDevices.filter((device) => device instanceof Speaker || !this.isVirtualDevice(device));

    // 4. Sort by label
    filteredDevices.sort((a, b) => {
      if (a.getId() === 'default') {
        return -1;
      }
      if (b.getId() === 'default') {
        return 1;
      }
      return a.getLabel().localeCompare(b.getLabel());
    });
    return filteredDevices;
  }

  /**
   * Checks if a device is a virtual device
   */
  private isVirtualDevice(device: MindMediaDevice): boolean {
    const virtualCameraLabels = [
      'OBS Virtual Camera',
      'ManyCam Virtual Webcam',
      'Snap Camera',
      'XSplit VCam',
      'EpocCam',
      'DroidCam',
      'CamTwist',
      'AlterCam',
      'Logitech Capture',
      'YouCam',
      'SparkoCam Virtual Camera',
    ];

    const virtualMicrophoneLabels = [
      'VB-Audio Virtual Cable',
      'VoiceMeeter',
      'DroidCam Virtual Audio',
      'EpocCam Microphone',
      'Virtual Audio Cable',
      'BlackHole',
    ];

    const isCamera = 'setResolution' in device;
    const label = device.getLabel();
    return isCamera ? virtualCameraLabels.includes(label) : virtualMicrophoneLabels.includes(label);
  }

  /**
   * Decorates a single device's display name
   */
  private decorateDeviceLabel(label: string): string {
    return label.replace(/\([0-9a-f]{4}:[0-9a-f]{4}\)$/, '');
  }

  /** Adds the real device label to description of the default fake device */
  private getDefaultDeviceDescription(deviceKind: MediaDeviceKind, deviceId: string): string {
    if (deviceId !== 'default') {
      return '';
    }

    // For cameras, there is no default device in navigator
    const deviceInfoByKind = this.mediaDevices.filter((device) => device.kind === deviceKind);
    if (deviceInfoByKind.length === 1) {
      return this.decorateDeviceLabel(deviceInfoByKind[0].label);
    }

    // Finding default device in navigator to pull groupId
    const deviceInfo = this.mediaDevices.find((device) => device.kind === deviceKind && device.deviceId === deviceId);
    if (!deviceInfo) {
      return '';
    }

    // Finding all devices in the same group
    const groupDevices = this.mediaDevices.filter(
      (device) => device.kind === deviceKind && device.groupId === deviceInfo.groupId
    );
    if (groupDevices.length === 0) {
      return '';
    }

    const realDevice = groupDevices.find((navDevice) => navDevice.deviceId !== 'default') || groupDevices[0];
    if (!realDevice) {
      return '';
    }

    return this.decorateDeviceLabel(realDevice.label);
  }
}
